import { useState, useCallback, useMemo, useEffect } from "react";

import BaseSearchWithFilters from "../../../../search-with-filters";
import { UniformFilterObject } from "../../../../search-with-filters/src/utils/types";
import {
    useSetFilter,
    useFilterOptions,
    useFilter,
    useSetSearch,
    useSearch
} from "../../context/hooks.ts";

export const SearchWithFilters = () => {
    const searchValueFromUrl = useSearch() || "";
    const [searchStringValue, setSearchStringValue] =
        useState(searchValueFromUrl);
    const [searchInputFieldFocused, setSearchInputFieldFocused] =
        useState(false);

    const setFilter = useSetFilter();
    const setSearch = useSetSearch();

    const appValue = useFilter("selectedProduct");
    const statusValue = useFilter("status");
    const categoryValue = useFilter("category");

    const appOptions = useFilterOptions("selectedProduct");
    const statusOptions = useFilterOptions("status");
    const categoryOptions = useFilterOptions("category");

    const filterObject = useMemo<UniformFilterObject>(() => {
        // Order matches column headers: Category, Status, Apps
        return {
            category: categoryOptions.reduce((acc, opt) => {
                const isSelected = categoryValue.includes(opt.value);
                return {
                    ...acc,
                    [opt.label]: {
                        committedActive: isSelected,
                        stagedActive: isSelected
                    }
                };
            }, {}),
            status: statusOptions.reduce((acc, opt) => {
                const isSelected = statusValue.includes(opt.value);
                return {
                    ...acc,
                    [opt.label]: {
                        committedActive: isSelected,
                        stagedActive: isSelected
                    }
                };
            }, {}),
            app: appOptions.reduce((acc, opt) => {
                // Check if the option's value is in the filter values from URL
                const isSelected = appValue.includes(opt.value);
                return {
                    ...acc,
                    [opt.label]: {
                        committedActive: isSelected,
                        stagedActive: isSelected
                    }
                };
            }, {})
        };
    }, [
        categoryOptions,
        categoryValue,
        statusOptions,
        statusValue,
        appOptions,
        appValue
    ]);

    const [filterObjectState, setFilterObjectState] =
        useState<UniformFilterObject>(filterObject);

    // Sync search string value with URL params
    useEffect(() => {
        setSearchStringValue(searchValueFromUrl);
    }, [searchValueFromUrl]);

    // Sync filter object state with URL params
    useEffect(() => {
        setFilterObjectState(filterObject);
    }, [filterObject]);

    const clearSearchField = useCallback(() => {
        setSearchStringValue("");
        setSearchInputFieldFocused(false);
        setSearch("");
    }, [setSearch]);

    const runSearchWithFilters = useCallback(
        (filterObj: UniformFilterObject, searchTerm: string) => {
            setFilterObjectState(filterObj);

            // search
            setSearch(searchTerm);

            // app (selectedProduct) filter
            const appFilterValue: string[] = [];
            Object.entries(filterObj.app).map(
                ([label, { committedActive }]) => {
                    if (committedActive) {
                        const option = appOptions.find(
                            (opt) => opt.label === label
                        );
                        if (option) {
                            appFilterValue.push(option.value);
                        }
                    }
                }
            );
            setFilter("selectedProduct", appFilterValue);

            // category filter
            const categoryFilterValue: string[] = [];
            Object.entries(filterObj.category).map(
                ([label, { committedActive }]) => {
                    if (committedActive) {
                        const option = categoryOptions.find(
                            (opt) => opt.label === label
                        );
                        if (option) {
                            categoryFilterValue.push(option.value);
                        }
                    }
                }
            );
            setFilter("category", categoryFilterValue);

            // status filter
            const statusFilterValue: string[] = [];
            Object.entries(filterObj.status).map(
                ([label, { committedActive }]) => {
                    if (committedActive) {
                        const option = statusOptions.find(
                            (opt) => opt.label === label
                        );
                        if (option) {
                            statusFilterValue.push(option.value);
                        }
                    }
                }
            );
            setFilter("status", statusFilterValue);
        },
        [appOptions, categoryOptions, statusOptions, setFilter, setSearch]
    );

    return (
        <BaseSearchWithFilters
            filterObject={filterObjectState}
            searchStringValue={searchStringValue}
            searchInputFieldFocused={searchInputFieldFocused}
            setSearchInputFieldFocused={setSearchInputFieldFocused}
            setFilterObjectValue={setFilterObjectState}
            setSearchString={setSearchStringValue}
            clearSearchField={clearSearchField}
            runSearchWithFilters={runSearchWithFilters}
        />
    );
};
